import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { weeksBeforeContingentPackageExpireAlertShouldBeShown } from '@common';

import {
  AuthStore,
  BookingStore,
  BookingSubscription,
  BOOKING_SUBSCRIPTIONS_FILTER_TYPE,
  InstallationsStore,
  RemoteConfigStore,
} from '@store';

import { SubscriptionPlan, SubscriptionStatus } from '@vi-iot/client';

import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-booking-overview-subscription-per-installation',
  templateUrl: './booking-overview-subscription-per-installation.component.html',
  styleUrls: ['./booking-overview-subscription-per-installation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingOverviewSubscriptionPerInstallationComponent implements OnInit, OnDestroy {
  readonly filterType = BOOKING_SUBSCRIPTIONS_FILTER_TYPE;

  allBookingSubscriptions: BookingSubscription[];
  unregisteredSubscriptions: BookingSubscription[] = [];
  activeSubscriptions: BookingSubscription[] = [];
  allExpiredSubscriptions: BookingSubscription[] = [];
  displayedExpiredSubscriptions: BookingSubscription[] = [];
  proSubscriptions: BookingSubscription[] = [];
  plusSubscriptions: BookingSubscription[] = [];
  proAndPlusSubscriptions: BookingSubscription[] = [];
  trialSubscriptions: BookingSubscription[] = [];
  automaticExtensionSubscriptions: BookingSubscription[] = [];
  searchSubscriptions: BookingSubscription[] = [];
  activeSubscriptionsFilter: BOOKING_SUBSCRIPTIONS_FILTER_TYPE =
    BOOKING_SUBSCRIPTIONS_FILTER_TYPE.ALL;
  isCancelledPackagesShown = true;
  searchTerm = '';
  showAllAndPlusSubscriptionsFilters = true;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationsStore: InstallationsStore,
    public bookingStore: BookingStore,
    public remoteConfigStore: RemoteConfigStore,
    private authStore: AuthStore,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.bookingStore.getContingentSubscriptions();
    this.authStore.isCompanyFromNetherlandsOrPoland$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isCompanyFromNetherlandsOrPoland) => {
        this.showAllAndPlusSubscriptionsFilters = !isCompanyFromNetherlandsOrPoland;

        if (isCompanyFromNetherlandsOrPoland) {
          this.activeSubscriptionsFilter = BOOKING_SUBSCRIPTIONS_FILTER_TYPE.PRO;
        }
      });

    this.bookingStore.subscriptions$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((subscriptions) => {
        this.allBookingSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            subscription.status?.toLowerCase() !== SubscriptionStatus.expired
        );
        this.filterActiveSubscriptions();
        this.cdr.detectChanges();

        this.allExpiredSubscriptions = subscriptions.filter(
          (subscription) => subscription.status?.toLowerCase() === SubscriptionStatus.expired
        );
        this.displayedExpiredSubscriptions = this.allExpiredSubscriptions;

        this.proAndPlusSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            (subscription.plan === SubscriptionPlan.Pro ||
              subscription.plan === SubscriptionPlan.Plus) &&
            subscription.status?.toLowerCase() !== SubscriptionStatus.expired &&
            subscription.registeredInstallation &&
            !subscription.trial
        );

        this.proSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            subscription.plan === SubscriptionPlan.Pro &&
            subscription.status?.toLowerCase() !== SubscriptionStatus.expired &&
            subscription.registeredInstallation &&
            !subscription.trial
        );

        this.plusSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            subscription.plan === SubscriptionPlan.Plus &&
            subscription.status?.toLowerCase() !== SubscriptionStatus.expired &&
            subscription.registeredInstallation &&
            !subscription.trial
        );

        this.trialSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            subscription.trial && subscription.status?.toLowerCase() !== SubscriptionStatus.expired
        );

        this.automaticExtensionSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            subscription.status?.toLowerCase() === SubscriptionStatus.active &&
            this.isAutomaticExtensionSubscription(subscription)
        );

        this.unregisteredSubscriptions = subscriptions.filter(
          (subscription: BookingSubscription) =>
            subscription.status?.toLowerCase() !== SubscriptionStatus.expired &&
            !subscription.registeredInstallation &&
            !subscription.trial
        );
      });
  }

  setFilter(subscriptionFilter: BOOKING_SUBSCRIPTIONS_FILTER_TYPE): void {
    this.activeSubscriptionsFilter = subscriptionFilter;
    this.filterActiveSubscriptions();
  }

  showCancelledPackages(): void {
    this.isCancelledPackagesShown = !this.isCancelledPackagesShown;
  }

  search(text: string): void {
    const textLowercase = text.toLowerCase();
    const activeSubscriptionsFuse = this.allBookingSubscriptions.filter(
      (subscription) =>
        subscription.subscribedBy?.toLowerCase().includes(textLowercase) ||
        subscription.boilerId?.toLowerCase().includes(textLowercase) ||
        subscription.ownerName?.toLowerCase().includes(textLowercase) ||
        subscription.subscribedByName?.toLowerCase().includes(textLowercase) ||
        subscription.marketingName?.toLowerCase().includes(textLowercase)
    );
    const expiredSubscriptionsFuse = this.allExpiredSubscriptions.filter(
      (subscription) =>
        subscription.subscribedBy?.toLowerCase().includes(textLowercase) ||
        subscription.boilerId?.toLowerCase().includes(textLowercase) ||
        subscription.ownerName?.toLowerCase().includes(textLowercase) ||
        subscription.subscribedByName?.toLowerCase().includes(textLowercase) ||
        subscription.marketingName?.toLowerCase().includes(textLowercase)
    );

    this.searchTerm = text;

    if (!this.searchTerm) {
      this.setDefaultSubscriptions();

      return;
    }

    this.searchSubscriptions = activeSubscriptionsFuse;
    this.activeSubscriptions = activeSubscriptionsFuse;
    this.displayedExpiredSubscriptions = expiredSubscriptionsFuse;
    this.filterActiveSubscriptions();
  }

  setDefaultSubscriptions(): void {
    this.searchSubscriptions = [];
    this.filterActiveSubscriptions();
    this.displayedExpiredSubscriptions = this.allExpiredSubscriptions;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private filterActiveSubscriptions(): void {
    const subscriptionsToFilter: BookingSubscription[] = this.searchTerm
      ? this.searchSubscriptions
      : this.allBookingSubscriptions;

    this.activeSubscriptions = subscriptionsToFilter.filter((subscription) => {
      const isNotExpired = subscription.status?.toLowerCase() !== SubscriptionStatus.expired;
      const isFilterAll =
        this.activeSubscriptionsFilter === BOOKING_SUBSCRIPTIONS_FILTER_TYPE.ALL &&
        subscription.registeredInstallation &&
        !subscription.trial;
      const isMatchingFilter =
        subscription.plan.toString() === this.activeSubscriptionsFilter &&
        subscription.registeredInstallation &&
        !subscription.trial;
      const isAutomaticExtension =
        this.activeSubscriptionsFilter === BOOKING_SUBSCRIPTIONS_FILTER_TYPE.AUTOMATIC_EXTENSION &&
        this.isAutomaticExtensionSubscription(subscription);
      const isTrial =
        this.activeSubscriptionsFilter === BOOKING_SUBSCRIPTIONS_FILTER_TYPE.TRIAL &&
        subscription.trial;
      const isCancelledPackageShown = this.isCancelledPackagesShown
        ? true
        : subscription.status?.toLowerCase() === SubscriptionStatus.active;

      return (
        isNotExpired &&
        (isFilterAll || isMatchingFilter || isAutomaticExtension || isTrial) &&
        isCancelledPackageShown
      );
    });
  }

  private isAutomaticExtensionSubscription(subscription: BookingSubscription): boolean {
    return (
      subscription.plan.toString() === SubscriptionPlan.Pro &&
      subscription.originalBundleInvoice &&
      moment(subscription.expirationDate).isBefore(
        moment().add(weeksBeforeContingentPackageExpireAlertShouldBeShown, 'weeks')
      ) &&
      !subscription.trial &&
      subscription.registeredInstallation
    );
  }
}
